import parsePhoneNumberLib, {
  getCountryCallingCode,
  validatePhoneNumberLength as validateLength,
  isValidPhoneNumber as isValidLib,
  type CountryCode,
} from 'libphonenumber-js'

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2 code */
  code: string
  /** English name */
  name: string
  /** Arabic name */
  nameAr: string
  /** Dial code with + prefix */
  dialCode: string
  /** Flag emoji */
  flag: string
}

/** Convert ISO country code to flag emoji */
export function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

/** Default country code for phone inputs */
export const DEFAULT_PHONE_COUNTRY = 'SA'

// GCC countries first, then sorted alphabetically
export const phoneCountries: PhoneCountry[] = [
  // GCC countries (prioritized)
  { code: 'SA', name: 'Saudi Arabia', nameAr: 'السعودية', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'United Arab Emirates', nameAr: 'الإمارات', dialCode: '+971', flag: '🇦🇪' },
  { code: 'BH', name: 'Bahrain', nameAr: 'البحرين', dialCode: '+973', flag: '🇧🇭' },
  { code: 'KW', name: 'Kuwait', nameAr: 'الكويت', dialCode: '+965', flag: '🇰🇼' },
  { code: 'OM', name: 'Oman', nameAr: 'عمان', dialCode: '+968', flag: '🇴🇲' },
  { code: 'QA', name: 'Qatar', nameAr: 'قطر', dialCode: '+974', flag: '🇶🇦' },
  // Other Middle East / North Africa
  { code: 'EG', name: 'Egypt', nameAr: 'مصر', dialCode: '+20', flag: '🇪🇬' },
  { code: 'JO', name: 'Jordan', nameAr: 'الأردن', dialCode: '+962', flag: '🇯🇴' },
  { code: 'IQ', name: 'Iraq', nameAr: 'العراق', dialCode: '+964', flag: '🇮🇶' },
  { code: 'LB', name: 'Lebanon', nameAr: 'لبنان', dialCode: '+961', flag: '🇱🇧' },
  { code: 'PS', name: 'Palestine', nameAr: 'فلسطين', dialCode: '+970', flag: '🇵🇸' },
  { code: 'SY', name: 'Syria', nameAr: 'سوريا', dialCode: '+963', flag: '🇸🇾' },
  { code: 'YE', name: 'Yemen', nameAr: 'اليمن', dialCode: '+967', flag: '🇾🇪' },
  { code: 'SD', name: 'Sudan', nameAr: 'السودان', dialCode: '+249', flag: '🇸🇩' },
  { code: 'LY', name: 'Libya', nameAr: 'ليبيا', dialCode: '+218', flag: '🇱🇾' },
  { code: 'TN', name: 'Tunisia', nameAr: 'تونس', dialCode: '+216', flag: '🇹🇳' },
  { code: 'DZ', name: 'Algeria', nameAr: 'الجزائر', dialCode: '+213', flag: '🇩🇿' },
  { code: 'MA', name: 'Morocco', nameAr: 'المغرب', dialCode: '+212', flag: '🇲🇦' },
  // Rest of the world (alphabetical)
  { code: 'AF', name: 'Afghanistan', nameAr: 'أفغانستان', dialCode: '+93', flag: '🇦🇫' },
  { code: 'AL', name: 'Albania', nameAr: 'ألبانيا', dialCode: '+355', flag: '🇦🇱' },
  { code: 'AD', name: 'Andorra', nameAr: 'أندورا', dialCode: '+376', flag: '🇦🇩' },
  { code: 'AO', name: 'Angola', nameAr: 'أنغولا', dialCode: '+244', flag: '🇦🇴' },
  { code: 'AR', name: 'Argentina', nameAr: 'الأرجنتين', dialCode: '+54', flag: '🇦🇷' },
  { code: 'AM', name: 'Armenia', nameAr: 'أرمينيا', dialCode: '+374', flag: '🇦🇲' },
  { code: 'AU', name: 'Australia', nameAr: 'أستراليا', dialCode: '+61', flag: '🇦🇺' },
  { code: 'AT', name: 'Austria', nameAr: 'النمسا', dialCode: '+43', flag: '🇦🇹' },
  { code: 'AZ', name: 'Azerbaijan', nameAr: 'أذربيجان', dialCode: '+994', flag: '🇦🇿' },
  { code: 'BD', name: 'Bangladesh', nameAr: 'بنغلاديش', dialCode: '+880', flag: '🇧🇩' },
  { code: 'BY', name: 'Belarus', nameAr: 'بيلاروسيا', dialCode: '+375', flag: '🇧🇾' },
  { code: 'BE', name: 'Belgium', nameAr: 'بلجيكا', dialCode: '+32', flag: '🇧🇪' },
  { code: 'BJ', name: 'Benin', nameAr: 'بنين', dialCode: '+229', flag: '🇧🇯' },
  { code: 'BO', name: 'Bolivia', nameAr: 'بوليفيا', dialCode: '+591', flag: '🇧🇴' },
  { code: 'BA', name: 'Bosnia and Herzegovina', nameAr: 'البوسنة والهرسك', dialCode: '+387', flag: '🇧🇦' },
  { code: 'BR', name: 'Brazil', nameAr: 'البرازيل', dialCode: '+55', flag: '🇧🇷' },
  { code: 'BN', name: 'Brunei', nameAr: 'بروناي', dialCode: '+673', flag: '🇧🇳' },
  { code: 'BG', name: 'Bulgaria', nameAr: 'بلغاريا', dialCode: '+359', flag: '🇧🇬' },
  { code: 'KH', name: 'Cambodia', nameAr: 'كمبوديا', dialCode: '+855', flag: '🇰🇭' },
  { code: 'CM', name: 'Cameroon', nameAr: 'الكاميرون', dialCode: '+237', flag: '🇨🇲' },
  { code: 'CA', name: 'Canada', nameAr: 'كندا', dialCode: '+1', flag: '🇨🇦' },
  { code: 'CL', name: 'Chile', nameAr: 'تشيلي', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CN', name: 'China', nameAr: 'الصين', dialCode: '+86', flag: '🇨🇳' },
  { code: 'CO', name: 'Colombia', nameAr: 'كولومبيا', dialCode: '+57', flag: '🇨🇴' },
  { code: 'CD', name: 'Congo (DRC)', nameAr: 'الكونغو الديمقراطية', dialCode: '+243', flag: '🇨🇩' },
  { code: 'CR', name: 'Costa Rica', nameAr: 'كوستاريكا', dialCode: '+506', flag: '🇨🇷' },
  { code: 'HR', name: 'Croatia', nameAr: 'كرواتيا', dialCode: '+385', flag: '🇭🇷' },
  { code: 'CU', name: 'Cuba', nameAr: 'كوبا', dialCode: '+53', flag: '🇨🇺' },
  { code: 'CY', name: 'Cyprus', nameAr: 'قبرص', dialCode: '+357', flag: '🇨🇾' },
  { code: 'CZ', name: 'Czech Republic', nameAr: 'التشيك', dialCode: '+420', flag: '🇨🇿' },
  { code: 'DK', name: 'Denmark', nameAr: 'الدنمارك', dialCode: '+45', flag: '🇩🇰' },
  { code: 'DJ', name: 'Djibouti', nameAr: 'جيبوتي', dialCode: '+253', flag: '🇩🇯' },
  { code: 'EC', name: 'Ecuador', nameAr: 'الإكوادور', dialCode: '+593', flag: '🇪🇨' },
  { code: 'ER', name: 'Eritrea', nameAr: 'إريتريا', dialCode: '+291', flag: '🇪🇷' },
  { code: 'EE', name: 'Estonia', nameAr: 'إستونيا', dialCode: '+372', flag: '🇪🇪' },
  { code: 'ET', name: 'Ethiopia', nameAr: 'إثيوبيا', dialCode: '+251', flag: '🇪🇹' },
  { code: 'FI', name: 'Finland', nameAr: 'فنلندا', dialCode: '+358', flag: '🇫🇮' },
  { code: 'FR', name: 'France', nameAr: 'فرنسا', dialCode: '+33', flag: '🇫🇷' },
  { code: 'GE', name: 'Georgia', nameAr: 'جورجيا', dialCode: '+995', flag: '🇬🇪' },
  { code: 'DE', name: 'Germany', nameAr: 'ألمانيا', dialCode: '+49', flag: '🇩🇪' },
  { code: 'GH', name: 'Ghana', nameAr: 'غانا', dialCode: '+233', flag: '🇬🇭' },
  { code: 'GR', name: 'Greece', nameAr: 'اليونان', dialCode: '+30', flag: '🇬🇷' },
  { code: 'GT', name: 'Guatemala', nameAr: 'غواتيمالا', dialCode: '+502', flag: '🇬🇹' },
  { code: 'HN', name: 'Honduras', nameAr: 'هندوراس', dialCode: '+504', flag: '🇭🇳' },
  { code: 'HK', name: 'Hong Kong', nameAr: 'هونغ كونغ', dialCode: '+852', flag: '🇭🇰' },
  { code: 'HU', name: 'Hungary', nameAr: 'المجر', dialCode: '+36', flag: '🇭🇺' },
  { code: 'IS', name: 'Iceland', nameAr: 'آيسلندا', dialCode: '+354', flag: '🇮🇸' },
  { code: 'IN', name: 'India', nameAr: 'الهند', dialCode: '+91', flag: '🇮🇳' },
  { code: 'ID', name: 'Indonesia', nameAr: 'إندونيسيا', dialCode: '+62', flag: '🇮🇩' },
  { code: 'IR', name: 'Iran', nameAr: 'إيران', dialCode: '+98', flag: '🇮🇷' },
  { code: 'IE', name: 'Ireland', nameAr: 'أيرلندا', dialCode: '+353', flag: '🇮🇪' },
  { code: 'IL', name: 'Israel', nameAr: 'إسرائيل', dialCode: '+972', flag: '🇮🇱' },
  { code: 'IT', name: 'Italy', nameAr: 'إيطاليا', dialCode: '+39', flag: '🇮🇹' },
  { code: 'JP', name: 'Japan', nameAr: 'اليابان', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KZ', name: 'Kazakhstan', nameAr: 'كازاخستان', dialCode: '+7', flag: '🇰🇿' },
  { code: 'KE', name: 'Kenya', nameAr: 'كينيا', dialCode: '+254', flag: '🇰🇪' },
  { code: 'KR', name: 'South Korea', nameAr: 'كوريا الجنوبية', dialCode: '+82', flag: '🇰🇷' },
  { code: 'XK', name: 'Kosovo', nameAr: 'كوسوفو', dialCode: '+383', flag: '🇽🇰' },
  { code: 'LV', name: 'Latvia', nameAr: 'لاتفيا', dialCode: '+371', flag: '🇱🇻' },
  { code: 'LT', name: 'Lithuania', nameAr: 'ليتوانيا', dialCode: '+370', flag: '🇱🇹' },
  { code: 'LU', name: 'Luxembourg', nameAr: 'لوكسمبورغ', dialCode: '+352', flag: '🇱🇺' },
  { code: 'MK', name: 'North Macedonia', nameAr: 'مقدونيا الشمالية', dialCode: '+389', flag: '🇲🇰' },
  { code: 'MY', name: 'Malaysia', nameAr: 'ماليزيا', dialCode: '+60', flag: '🇲🇾' },
  { code: 'MV', name: 'Maldives', nameAr: 'المالديف', dialCode: '+960', flag: '🇲🇻' },
  { code: 'ML', name: 'Mali', nameAr: 'مالي', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MT', name: 'Malta', nameAr: 'مالطا', dialCode: '+356', flag: '🇲🇹' },
  { code: 'MR', name: 'Mauritania', nameAr: 'موريتانيا', dialCode: '+222', flag: '🇲🇷' },
  { code: 'MU', name: 'Mauritius', nameAr: 'موريشيوس', dialCode: '+230', flag: '🇲🇺' },
  { code: 'MX', name: 'Mexico', nameAr: 'المكسيك', dialCode: '+52', flag: '🇲🇽' },
  { code: 'MD', name: 'Moldova', nameAr: 'مولدوفا', dialCode: '+373', flag: '🇲🇩' },
  { code: 'MC', name: 'Monaco', nameAr: 'موناكو', dialCode: '+377', flag: '🇲🇨' },
  { code: 'MN', name: 'Mongolia', nameAr: 'منغوليا', dialCode: '+976', flag: '🇲🇳' },
  { code: 'ME', name: 'Montenegro', nameAr: 'الجبل الأسود', dialCode: '+382', flag: '🇲🇪' },
  { code: 'MZ', name: 'Mozambique', nameAr: 'موزمبيق', dialCode: '+258', flag: '🇲🇿' },
  { code: 'MM', name: 'Myanmar', nameAr: 'ميانمار', dialCode: '+95', flag: '🇲🇲' },
  { code: 'NP', name: 'Nepal', nameAr: 'نيبال', dialCode: '+977', flag: '🇳🇵' },
  { code: 'NL', name: 'Netherlands', nameAr: 'هولندا', dialCode: '+31', flag: '🇳🇱' },
  { code: 'NZ', name: 'New Zealand', nameAr: 'نيوزيلندا', dialCode: '+64', flag: '🇳🇿' },
  { code: 'NI', name: 'Nicaragua', nameAr: 'نيكاراغوا', dialCode: '+505', flag: '🇳🇮' },
  { code: 'NG', name: 'Nigeria', nameAr: 'نيجيريا', dialCode: '+234', flag: '🇳🇬' },
  { code: 'NO', name: 'Norway', nameAr: 'النرويج', dialCode: '+47', flag: '🇳🇴' },
  { code: 'PK', name: 'Pakistan', nameAr: 'باكستان', dialCode: '+92', flag: '🇵🇰' },
  { code: 'PA', name: 'Panama', nameAr: 'بنما', dialCode: '+507', flag: '🇵🇦' },
  { code: 'PY', name: 'Paraguay', nameAr: 'باراغواي', dialCode: '+595', flag: '🇵🇾' },
  { code: 'PE', name: 'Peru', nameAr: 'بيرو', dialCode: '+51', flag: '🇵🇪' },
  { code: 'PH', name: 'Philippines', nameAr: 'الفلبين', dialCode: '+63', flag: '🇵🇭' },
  { code: 'PL', name: 'Poland', nameAr: 'بولندا', dialCode: '+48', flag: '🇵🇱' },
  { code: 'PT', name: 'Portugal', nameAr: 'البرتغال', dialCode: '+351', flag: '🇵🇹' },
  { code: 'RO', name: 'Romania', nameAr: 'رومانيا', dialCode: '+40', flag: '🇷🇴' },
  { code: 'RU', name: 'Russia', nameAr: 'روسيا', dialCode: '+7', flag: '🇷🇺' },
  { code: 'RW', name: 'Rwanda', nameAr: 'رواندا', dialCode: '+250', flag: '🇷🇼' },
  { code: 'SN', name: 'Senegal', nameAr: 'السنغال', dialCode: '+221', flag: '🇸🇳' },
  { code: 'RS', name: 'Serbia', nameAr: 'صربيا', dialCode: '+381', flag: '🇷🇸' },
  { code: 'SG', name: 'Singapore', nameAr: 'سنغافورة', dialCode: '+65', flag: '🇸🇬' },
  { code: 'SK', name: 'Slovakia', nameAr: 'سلوفاكيا', dialCode: '+421', flag: '🇸🇰' },
  { code: 'SI', name: 'Slovenia', nameAr: 'سلوفينيا', dialCode: '+386', flag: '🇸🇮' },
  { code: 'SO', name: 'Somalia', nameAr: 'الصومال', dialCode: '+252', flag: '🇸🇴' },
  { code: 'ZA', name: 'South Africa', nameAr: 'جنوب أفريقيا', dialCode: '+27', flag: '🇿🇦' },
  { code: 'ES', name: 'Spain', nameAr: 'إسبانيا', dialCode: '+34', flag: '🇪🇸' },
  { code: 'LK', name: 'Sri Lanka', nameAr: 'سريلانكا', dialCode: '+94', flag: '🇱🇰' },
  { code: 'SE', name: 'Sweden', nameAr: 'السويد', dialCode: '+46', flag: '🇸🇪' },
  { code: 'CH', name: 'Switzerland', nameAr: 'سويسرا', dialCode: '+41', flag: '🇨🇭' },
  { code: 'TW', name: 'Taiwan', nameAr: 'تايوان', dialCode: '+886', flag: '🇹🇼' },
  { code: 'TZ', name: 'Tanzania', nameAr: 'تنزانيا', dialCode: '+255', flag: '🇹🇿' },
  { code: 'TH', name: 'Thailand', nameAr: 'تايلاند', dialCode: '+66', flag: '🇹🇭' },
  { code: 'TR', name: 'Turkey', nameAr: 'تركيا', dialCode: '+90', flag: '🇹🇷' },
  { code: 'TM', name: 'Turkmenistan', nameAr: 'تركمانستان', dialCode: '+993', flag: '🇹🇲' },
  { code: 'UG', name: 'Uganda', nameAr: 'أوغندا', dialCode: '+256', flag: '🇺🇬' },
  { code: 'UA', name: 'Ukraine', nameAr: 'أوكرانيا', dialCode: '+380', flag: '🇺🇦' },
  { code: 'GB', name: 'United Kingdom', nameAr: 'المملكة المتحدة', dialCode: '+44', flag: '🇬🇧' },
  { code: 'US', name: 'United States', nameAr: 'الولايات المتحدة', dialCode: '+1', flag: '🇺🇸' },
  { code: 'UY', name: 'Uruguay', nameAr: 'أوروغواي', dialCode: '+598', flag: '🇺🇾' },
  { code: 'UZ', name: 'Uzbekistan', nameAr: 'أوزبكستان', dialCode: '+998', flag: '🇺🇿' },
  { code: 'VE', name: 'Venezuela', nameAr: 'فنزويلا', dialCode: '+58', flag: '🇻🇪' },
  { code: 'VN', name: 'Vietnam', nameAr: 'فيتنام', dialCode: '+84', flag: '🇻🇳' },
  { code: 'ZM', name: 'Zambia', nameAr: 'زامبيا', dialCode: '+260', flag: '🇿🇲' },
  { code: 'ZW', name: 'Zimbabwe', nameAr: 'زيمبابوي', dialCode: '+263', flag: '🇿🇼' },
]

// Pre-build lookup map for fast access
const byCode = new Map(phoneCountries.map((c) => [c.code, c]))

/** Get a country by ISO code */
export function getPhoneCountryByCode(isoCode: string): PhoneCountry | undefined {
  return byCode.get(isoCode.toUpperCase())
}

/** GCC country codes for search prioritization */
const GCC_CODES = new Set(['SA', 'AE', 'BH', 'KW', 'OM', 'QA'])

/** Search countries by name, Arabic name, ISO code, or dial code */
export function searchPhoneCountries(query: string, limit = 20): PhoneCountry[] {
  if (!query.trim()) return phoneCountries.slice(0, limit)

  const q = query.toLowerCase().trim()
  const matches = phoneCountries.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.nameAr.includes(q) ||
      c.code.toLowerCase().includes(q) ||
      c.dialCode.includes(q)
  )

  // Sort: GCC first, then alphabetical
  matches.sort((a, b) => {
    const aGCC = GCC_CODES.has(a.code) ? 0 : 1
    const bGCC = GCC_CODES.has(b.code) ? 0 : 1
    if (aGCC !== bGCC) return aGCC - bGCC
    return a.name.localeCompare(b.name)
  })

  return matches.slice(0, limit)
}

// ---------------------------------------------------------------------------
// libphonenumber-js powered parse / format / validate
// ---------------------------------------------------------------------------

/**
 * Parse a combined phone value (e.g., "+966501234567") into country code and local number.
 * Uses libphonenumber-js for accurate parsing. Falls back to SA when unrecognized.
 */
export function parsePhoneValue(value: string | null | undefined): {
  countryCode: string
  localNumber: string
} {
  if (!value?.trim()) {
    return { countryCode: '', localNumber: '' }
  }

  const cleaned = value.trim()

  try {
    const parsed = parsePhoneNumberLib(cleaned)
    if (parsed?.country) {
      return {
        countryCode: parsed.country,
        localNumber: parsed.nationalNumber,
      }
    }
  } catch {
    // Fall through to manual fallback
  }

  // If libphonenumber can't parse it (e.g. partial number), try prefix matching
  if (cleaned.startsWith('+')) {
    for (const country of phoneCountries) {
      if (cleaned.startsWith(country.dialCode)) {
        const local = cleaned.slice(country.dialCode.length).replace(/^[\s\-]+/, '')
        return { countryCode: country.code, localNumber: local }
      }
    }
  }

  // No recognized prefix — return as-is with default country
  return { countryCode: DEFAULT_PHONE_COUNTRY, localNumber: cleaned }
}

/**
 * Combine country code and local number into an E.164 phone string.
 * Returns empty string if no local number.
 */
export function formatPhoneValue(countryCode: string, localNumber: string): string {
  const cleaned = localNumber.replace(/[\s\-()]+/g, '').trim()
  if (!cleaned) return ''

  try {
    // Strip leading zero (common local format)
    const number = cleaned.startsWith('0') ? cleaned.slice(1) : cleaned
    const dialCode = `+${getCountryCallingCode(countryCode as CountryCode)}`
    return `${dialCode}${number}`
  } catch {
    // Fallback if country code not recognized by lib
    const country = getPhoneCountryByCode(countryCode)
    if (!country) return cleaned
    const number = cleaned.startsWith('0') ? cleaned.slice(1) : cleaned
    return `${country.dialCode}${number}`
  }
}

/**
 * Validate a phone number's length for a given country.
 * Returns undefined if valid, or an error string if invalid.
 */
export function validatePhoneNumberLength(
  localNumber: string,
  countryCode: string
): 'TOO_SHORT' | 'TOO_LONG' | 'INVALID_LENGTH' | undefined {
  const cleaned = localNumber.replace(/[\s\-()]+/g, '').trim()
  if (!cleaned) return undefined // Empty is not a length error (use required validation separately)

  try {
    const combined = formatPhoneValue(countryCode, cleaned)
    if (!combined) return undefined
    const result = validateLength(combined, countryCode as CountryCode)
    if (result === 'NOT_A_NUMBER' || result === 'INVALID_COUNTRY') return 'INVALID_LENGTH'
    return result as 'TOO_SHORT' | 'TOO_LONG' | undefined
  } catch {
    return undefined
  }
}

/**
 * Check if a combined phone value (e.g., "+966501234567") is a valid phone number.
 * Backward-compatible: also accepts numbers without '+' prefix.
 */
export function isValidPhoneNumber(value: string | null | undefined): boolean {
  if (!value?.trim()) return false
  const cleaned = value.trim()

  try {
    // If it already has a country prefix
    if (cleaned.startsWith('+')) {
      return isValidLib(cleaned)
    }
    // Try with default country
    return isValidLib(cleaned, DEFAULT_PHONE_COUNTRY as CountryCode)
  } catch {
    return false
  }
}

/**
 * R4: the phone country a property's own forms start on - its `properties.country`
 * when that is a known ISO-2 code (case-insensitive), else {@link DEFAULT_PHONE_COUNTRY}.
 */
export function phoneCountryFor(country: string | null | undefined): string {
  const code = typeof country === 'string' ? country.trim().toUpperCase() : ''
  return phoneCountries.some((c) => c.code === code) ? code : DEFAULT_PHONE_COUNTRY
}
